import { useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Archive, ChevronDown, ChevronRight, Eye, KeyRound, Pencil, Plus } from 'lucide-react';
import { formatCents, formatDate, todayIso, type Lease } from '@vuokramani/shared';
import { ContactValue, DetailList, Section, pageStyles as s } from '../../components/page.tsx';
import {
  Button,
  buttonClass,
  ComingSoon,
  Dialog,
  DialogActions,
  EmptyState,
  Notice,
  Panel,
  SelectField,
  Splash,
} from '../../components/ui.tsx';
import { useArchiveProperty, useLandlords } from '../../lib/properties.ts';
import { companyRows, homeRows } from '../shared/propertyRows.tsx';
import { ContactsList } from '../shared/ContactsList.tsx';
import { FilesManager } from '../shared/Files.tsx';
import { LeaseParties, LeaseTerms } from '../shared/LeaseView.tsx';
import { LeaseInspections } from '../shared/InspectionView.tsx';
import { EndedLeases, LeaseEnding } from './LeaseEnding.tsx';
import { LeaseTermsDialog } from './LeaseTermsDialog.tsx';
import { MaintenanceLog } from '../shared/Maintenance.tsx';
import { Occupants } from '../shared/Occupants.tsx';
import {
  activeLease,
  closingLease,
  lastEndedLease,
  useFiles,
  useLeases,
  useSetLeaseContract,
} from '../../lib/tenancy.ts';
import { EditCompanyDialog, EditHomeDialog } from './PropertyEditDialogs.tsx';
import { useCurrentProperty } from './PropertyLayout.tsx';

/**
 * Yleiskatsaus: asunto, taloyhtiö, vuokrasuhteen tiivistelmä ja yhteystiedot vuokralaiselle.
 * Silmä merkitsee tiedot, jotka näkyvät myös vuokralaiselle.
 */
export function PropertyOverviewTab() {
  const { t, i18n } = useTranslation();
  const p = useCurrentProperty();
  const [editing, setEditing] = useState<'home' | 'company' | null>(null);
  const isOwner = p.myRole === 'owner';
  const archived = Boolean(p.archivedAt);

  const editButton = (target: 'home' | 'company') =>
    isOwner && !archived ? (
      <Button variant="ghost" onClick={() => setEditing(target)}>
        <Pencil size={15} aria-hidden="true" />
        {t('edit.edit')}
      </Button>
    ) : undefined;

  return (
    <div className={s.stack}>
      <Section
        title={t('property.sections.home')}
        action={editButton('home')}
        note={
          <>
            <Eye size={15} aria-hidden="true" style={{ color: 'var(--accent)' }} />
            {t('property.sharedLegend')}
          </>
        }
      >
        <Panel>
          <DetailList rows={homeRows(p, t, i18n.language)} />
        </Panel>
      </Section>

      <Section title={t('property.sections.company')} action={editButton('company')}>
        <Panel>
          <DetailList rows={companyRows(p, t)} />
          <ContactsList propertyId={p.id} editable={isOwner && !archived} />
        </Panel>
      </Section>

      <LeaseSummary />

      <LandlordContactPreview />

      {isOwner && !archived && <ArchiveZone />}

      {editing === 'home' && <EditHomeDialog property={p} open onClose={() => setEditing(null)} />}
      {editing === 'company' && (
        <EditCompanyDialog property={p} open onClose={() => setEditing(null)} />
      )}
    </div>
  );
}

/** Yleiskatsauksen tiivistelmä voimassa olevasta vuokrasuhteesta. */
function LeaseSummary() {
  const { t, i18n } = useTranslation();
  const p = useCurrentProperty();
  const { data: leases } = useLeases(p.id);
  const lease = activeLease(leases);
  const link = (
    <Link
      to="vuokrasuhde"
      style={{
        fontWeight: 600,
        fontSize: 'var(--text-sm)',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 2,
      }}
    >
      {t('lease.open')}
      <ChevronRight size={15} aria-hidden="true" />
    </Link>
  );
  // Vuokralainen muuttanut pois, mutta vuokrasuhde on sulkematta (vakuus palauttamatta).
  const movedOut = lease?.endDate && lease.endDate < todayIso() ? lease.endDate : null;
  return (
    <Section title={t('property.tenancyTitle')} action={lease ? link : undefined}>
      {lease ? (
        <Panel>
          {movedOut && (
            <p className={s.movedOutNote}>
              {t('lease.movedOutPending', { date: formatDate(movedOut, i18n.language) })}{' '}
              {p.myRole === 'owner' && <Link to="vuokrasuhde">{t('lease.finishClosing')}</Link>}
            </p>
          )}
          <DetailList
            rows={[
              {
                label: t('lease.tenants', {
                  count: lease.parties.filter((x) => x.role === 'tenant').length,
                }),
                value: lease.parties
                  .filter((x) => x.role === 'tenant')
                  .map((x) => x.name)
                  .join(', '),
                shared: true,
              },
              {
                label: t('lease.rent'),
                value: t('lease.perMonth', { amount: formatCents(lease.rentCents, i18n.language) }),
                shared: true,
              },
              {
                label: t('lease.deposit'),
                value: formatCents(lease.depositCents, i18n.language),
                shared: true,
              },
              {
                label: t('lease.start'),
                value: formatDate(lease.startDate, i18n.language),
                shared: true,
              },
            ]}
          />
        </Panel>
      ) : leases ? (
        <Panel>
          <div className={s.vacantRow}>
            <span style={{ color: 'var(--muted)' }}>{t('lease.vacantShort')}</span>
            {p.myRole === 'owner' && !p.archivedAt && (
              <Link to="vuokrasuhde/uusi" className={buttonClass('primary')}>
                <Plus size={16} aria-hidden="true" />
                {t('newLease.title')}
              </Link>
            )}
          </div>
        </Panel>
      ) : null}
    </Section>
  );
}

/** Vuokrasuhde-välilehti: ehdot (lukittu allekirjoituksen jälkeen) ja osapuolet. */
export function LandlordTenancyTab() {
  const { t } = useTranslation();
  const p = useCurrentProperty();
  const { data: leases, isLoading } = useLeases(p.id);
  const [editing, setEditing] = useState(false);
  if (isLoading) return <Splash />;
  const lease = activeLease(leases);
  const closing = closingLease(leases);
  if (!lease) {
    return (
      <div className={s.stack}>
        <EmptyState
          icon={<KeyRound size={22} />}
          title={t('lease.vacantTitle')}
          text={t('lease.vacantText')}
          action={
            p.archivedAt ? undefined : (
              <Link to="uusi" className={buttonClass('primary')}>
                <Plus size={16} aria-hidden="true" />
                {t('newLease.title')}
              </Link>
            )
          }
        />
        {!p.archivedAt && lastEndedLease(leases) && (
          <Section title={t('vacant.nextTitle')}>
            <Panel>
              <ul className={s.nextSteps}>
                <li>
                  <Link to="..">{t('vacant.checkDetails')}</Link>
                </li>
                <li>
                  <Link to="../tarkastus">{t('vacant.previousInspection')}</Link>
                </li>
                <li>
                  <Link to="../tiedostot">{t('vacant.checkFiles')}</Link>
                </li>
                <li>
                  <Link to="uusi">{t('vacant.newLease')}</Link>
                </li>
              </ul>
            </Panel>
          </Section>
        )}
        <EndedLeases leases={leases ?? []} />
      </div>
    );
  }
  // Vuokralainen muuttanut pois, vuokrasuhde sulkematta: päättäminen ensin, muokkaukset pois.
  const movedOut = Boolean(lease.endDate && lease.endDate < todayIso());
  return (
    <div className={s.stack}>
      {movedOut && !p.archivedAt && <LeaseEnding lease={lease} />}
      <Section
        title={t('lease.terms')}
        action={
          p.archivedAt || movedOut ? undefined : (
            <Button variant="ghost" onClick={() => setEditing(true)}>
              <Pencil size={15} aria-hidden="true" />
              {t('terms.edit')}
            </Button>
          )
        }
      >
        <LeaseTerms lease={lease} />
      </Section>
      {editing && <LeaseTermsDialog lease={lease} onClose={() => setEditing(false)} />}
      {!p.archivedAt && <ContractPicker lease={lease} />}
      {closing && !p.archivedAt && <LeaseEnding lease={closing} previous />}
      <Section title={t('lease.parties')} note={t('lease.partiesNote')}>
        <LeaseParties lease={lease} manage={!p.archivedAt} />
      </Section>
      <Section title={t('occupants.title')} note={t('occupants.note')}>
        <Occupants
          lease={lease}
          editable={!p.archivedAt && !movedOut}
          canSetPets={!p.archivedAt && !movedOut}
        />
      </Section>
      {!p.archivedAt && !movedOut && <LeaseEnding lease={lease} />}
    </div>
  );
}

/**
 * Vuokrasopimuksen tiedosto Sopimukset-kansiosta. Liitetty sopimus jaetaan vuokralaiselle
 * ja säilyy historiassa ja zipissä vuokrasuhteen päätyttyäkin.
 */
function ContractPicker({ lease }: { lease: Lease }) {
  const { t } = useTranslation();
  const { data: files } = useFiles(lease.propertyId);
  const setContract = useSetLeaseContract(lease.propertyId, lease.id);
  const contracts = (files ?? []).filter((f) => f.folder === 'contract');
  const current = lease.contractFile?.id ?? '';
  return (
    <Section title={t('contract.title')} note={t('contract.note')}>
      <Panel>
        {contracts.length === 0 ? (
          <p style={{ color: 'var(--muted)' }}>
            {t('contract.noFiles')} <Link to="../tiedostot">{t('contract.toFiles')}</Link>
          </p>
        ) : (
          <SelectField
            label={t('contract.select')}
            value={current}
            disabled={setContract.isPending}
            onChange={(e) => setContract.mutate(e.target.value || null)}
          >
            <option value="">{t('contract.none')}</option>
            {contracts.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </SelectField>
        )}
      </Panel>
    </Section>
  );
}

/** Muutto- ja lopputarkastus voimassa olevalle vuokrasuhteelle. */
export function LandlordInspectionTab() {
  const { t, i18n } = useTranslation();
  const p = useCurrentProperty();
  const { data: leases, isLoading } = useLeases(p.id);
  if (isLoading) return <Splash />;
  const lease = activeLease(leases);
  if (!lease) {
    // Vapaana: edellisen vuokrasuhteen tarkastukset vain luettavina, vertailuksi seuraavaan.
    const previous = lastEndedLease(leases);
    if (!previous) {
      return (
        <EmptyState
          icon={<KeyRound size={22} />}
          title={t('lease.vacantTitle')}
          text={t('inspection.needsLease')}
        />
      );
    }
    return (
      <div className={s.stack}>
        <Notice>
          {t('inspection.previousLease', {
            tenants: previous.parties
              .filter((x) => x.role === 'tenant')
              .map((x) => x.name)
              .join(', '),
            date: previous.endDate ? formatDate(previous.endDate, i18n.language) : '–',
          })}
        </Notice>
        <LeaseInspections lease={previous} />
        <PastInspections leases={leases} exclude={previous.id} />
      </div>
    );
  }
  const closing = closingLease(leases);
  if (closing) {
    // Edellisen vuokrasuhteen lopputarkastus voi olla vielä kesken uuden rinnalla.
    return (
      <div className={s.stack}>
        <LeaseInspections lease={lease} />
        <Section
          title={t('inspection.previousTitle', {
            tenants: closing.parties
              .filter((x) => x.role === 'tenant')
              .map((x) => x.name)
              .join(', '),
          })}
        >
          <LeaseInspections lease={closing} />
        </Section>
        <PastInspections leases={leases} exclude={closing.id} />
      </div>
    );
  }
  return (
    <div className={s.stack}>
      <LeaseInspections lease={lease} />
      <PastInspections leases={leases} />
    </div>
  );
}

/**
 * Aiempien (päättyneiden) vuokrasuhteiden tarkastukset vain luettavina, uusin ensin. Vain
 * vuokranantajalle: tarkastukset ovat todisteita, joita voidaan tarvita myöhemmin. Avataan
 * yksi kerrallaan, jotta tarkastuksia ja kuvia ei ladata turhaan.
 */
function PastInspections({ leases, exclude }: { leases: Lease[] | undefined; exclude?: string }) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState<string | null>(null);
  const ended = (leases ?? [])
    .filter((l) => l.status === 'ended' && l.id !== exclude)
    .sort((a, b) => (b.endDate ?? '').localeCompare(a.endDate ?? ''));
  if (ended.length === 0) return null;
  return (
    <Section title={t('inspection.pastTitle')} note={t('inspection.pastNote')}>
      <Panel>
        <ul className={s.pastList}>
          {ended.map((l) => {
            const isOpen = open === l.id;
            return (
              <li key={l.id}>
                <button
                  type="button"
                  className={s.pastToggle}
                  aria-expanded={isOpen}
                  onClick={() => setOpen(isOpen ? null : l.id)}
                >
                  <span className={s.pastName}>
                    {l.parties
                      .filter((x) => x.role === 'tenant')
                      .map((x) => x.name)
                      .join(', ')}
                  </span>
                  <span className={s.pastDates}>
                    {formatDate(l.startDate, i18n.language)} –{' '}
                    {l.endDate ? formatDate(l.endDate, i18n.language) : ''}
                  </span>
                  <ChevronDown size={16} aria-hidden="true" className={s.pastChevron} />
                </button>
                {isOpen && (
                  <div className={s.pastBody}>
                    <LeaseInspections lease={l} />
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </Panel>
    </Section>
  );
}

/** Huoltokirja: remontit, korjaukset, hankinnat ja taloyhtiön työt. */
export function LandlordMaintenanceTab() {
  const { t } = useTranslation();
  const p = useCurrentProperty();
  return (
    <MaintenanceLog
      propertyId={p.id}
      editable={!p.archivedAt}
      emptyText={t('maintenance.emptyText')}
    />
  );
}

export function LandlordFilesTab() {
  const p = useCurrentProperty();
  return <FilesManager propertyId={p.id} />;
}

/** Näin vuokralainen näkee vuokranantajan yhteystiedot. */
function LandlordContactPreview() {
  const { t } = useTranslation();
  const p = useCurrentProperty();
  const { data: landlords } = useLandlords(p.id);
  return (
    <Section
      title={t('property.sections.contacts')}
      action={
        <Link to="/vuokranantaja/tili" style={{ fontWeight: 600, fontSize: 'var(--text-sm)' }}>
          {t('property.changeContacts')}
        </Link>
      }
      note={t('property.contactsNote')}
    >
      <Panel>
        <DetailList
          rows={(landlords ?? []).map((l, i) => ({
            label: i === 0 ? t('property.fields.landlord') : '',
            value: (
              <ContactValue name={l.name ?? t('property.noName')} phone={l.phone} email={l.email} />
            ),
            shared: true,
          }))}
        />
      </Panel>
    </Section>
  );
}

function ArchiveZone() {
  const { t } = useTranslation();
  const p = useCurrentProperty();
  const archive = useArchiveProperty(p.id);
  const [confirm, setConfirm] = useState(false);
  return (
    <>
      <div className={s.dangerZone}>
        <div className={s.dangerText}>
          <span className={s.dangerTitle}>{t('archive.title')}</span>
          <span style={{ color: 'var(--muted)', fontSize: 'var(--text-sm)' }}>
            {t('archive.text')}
          </span>
        </div>
        <Button onClick={() => setConfirm(true)}>
          <Archive size={16} aria-hidden="true" />
          {t('archive.action')}
        </Button>
      </div>
      <Dialog open={confirm} onClose={() => setConfirm(false)} title={t('archive.confirmTitle')}>
        <p>{t('archive.confirmText')}</p>
        <DialogActions>
          <Button variant="ghost" onClick={() => setConfirm(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            disabled={archive.isPending}
            onClick={() => archive.mutate('archive', { onSuccess: () => setConfirm(false) })}
          >
            {t('archive.action')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

/** Rakentamaton välilehti: kertoo, mitä tähän tulee. */
export function SoonTab({ textKey }: { textKey: string }) {
  const { t } = useTranslation();
  return <ComingSoon text={t(textKey)} />;
}
